import * as React from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useFormik } from 'formik';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as Device from 'react-device-detect';

import { useSession } from '@hooks';
import { refetchEventsVar } from '@context';
import { initBookingDateTime } from '@utils';
import { useCreateEventMutation, useListEventCategoriesQuery, SearchMyFriendsQuery } from '@graphql';
import initValues, { getMinimalFromDate } from './initValues';
import validationSchema from './validationSchema';
import { SearchParticipants } from './SearchParticipants';
import { Form } from '../../form';

import 'react-quill-new/dist/quill.snow.css';
import './quill.css';

interface EventFormDialogProps {
  variant: 'create';
}

export function EventFormDialog(props: Readonly<EventFormDialogProps>) {
  const { variant } = props;
  const { account } = useSession();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isFieldFocused, setIsFieldFocused] = React.useState(false);
  const [participants, setParticipants] = React.useState<SearchMyFriendsQuery['searchMyFriends']>([]);
  const [searchParams] = useSearchParams();
  const { eventId } = useParams();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('field');

  const defaultValues = initValues();

  const formik = useFormik({
    initialValues: initValues(),
    validationSchema: validationSchema(t),
    onSubmit: async ({ id, ...values }) => {
      // Sanitize description
      const sanitizedDescription = DOMPurify.sanitize(values.description || '');
      Object.assign(values, {
        description: sanitizedDescription.length ? sanitizedDescription : null,
      });

      if ('participantIds' in values && participants.length) {
        values.participantIds = participants.map((participant) => participant.id);
      }

      if ('categoryId' in values && !values.categoryId.length) {
        Object.assign(values, { categoryId: null });
      }

      if (account?.__typename === 'Consumer') {
        // force private event for consumer account
        values.isPrivate = true;
      }

      await createEvent({
        variables: {
          input: { ...values },
        },
      });
    },
  });

  const { data } = useListEventCategoriesQuery();

  const [createEvent, { loading: creating }] = useCreateEventMutation({
    onCompleted: ({ createEvent }) => {
      refetchEventsVar(true);
      handleClose();
      toast(t('event.toast.create.success', { ns: 'field' }), { type: 'success' });
    },
    onError(error) {
      toast.error(t('event.toast.create.error', { ns: 'field' }));
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      const initialValues = { ...defaultValues };

      if (account?.__typename === 'Spot') {
        const schedules = { opening: account.opening, closing: account.closing };
        const focusDate = searchParams.get('date') || undefined;
        const isToday = moment(focusDate).isSame(moment(), 'day');
        const dateInit =
          isToday && focusDate
            ? moment(focusDate)
                .set({
                  hour: moment().hour(),
                  minute: moment().minute(),
                  second: moment().second(),
                  millisecond: moment().millisecond(),
                })
                .add(1, 'hour')
            : moment(focusDate);

        const initialStartTime = initBookingDateTime(schedules, dateInit);
        initialValues.from = initialStartTime;
        initialValues.to = moment(initialStartTime).add(3, 'hours');
      } else if (searchParams.get('date')) {
        initialValues.from = getMinimalFromDate(moment(searchParams.get('date')));
        initialValues.to = moment(initialValues.from).clone().add(1, 'hour');
      }

      formik.resetForm({ values: initValues(initialValues) });
    }

    return () => {
      formik.resetForm({ values: initValues(defaultValues) });
    };
  }, [isOpen, searchParams]);

  React.useEffect(() => {
    if (variant === 'create') {
      handleOpen();
    }
  }, [variant]);

  React.useEffect(() => {
    // seek for the h3 button
    const h3Button = document.querySelector('.ql-picker-item[data-value="3"]');

    // Verify if the element exists before modifying it
    if (h3Button) {
      h3Button.setAttribute('data-label', 'Titre'); // Modifier l'étiquette
      h3Button.innerHTML = '';
    }
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleSubmit = () => {
    formik.submitForm();
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Escape'].includes(event.key)) {
      (event.target as HTMLElement).blur();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    formik.resetForm({ values: initValues(defaultValues) });

    navigate(state.from || -1, { preventScrollReset: true, replace: true });
  };

  if (!data?.eventCategories?.length) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      scroll="body"
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={Device.isMobileOnly}
    >
      <Form
        sx={{
          minHeight: '90dvh',
        }}
      >
        <Form.Header
          type="event"
          action={variant}
          startDate={moment(formik.values.from)}
          isDateClosed={false}
          handleClose={handleClose}
        />
        <form>
          <Form.Body>
            {account?.__typename === 'Spot' && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      required
                      name="isPrivate"
                      checked={formik.values.isPrivate}
                      onChange={formik.handleChange}
                      slotProps={{ input: { 'aria-label': '' } }}
                      color="error"
                    />
                  }
                  label={t(`event.label.isPrivate.${formik.values.isPrivate}`)}
                />
              </FormGroup>
            )}
            <TextField
              required
              label={t('event.label.title')}
              name="title"
              value={formik.values.title}
              onFocus={() => setIsFieldFocused(true)}
              onBlur={() => setIsFieldFocused(false)}
              onChange={formik.handleChange}
              onKeyDown={handleOnKeyDown}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title ? String(formik.errors.title) : null}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <DateTimePicker
                label={t('event.label.from') + ' *'}
                value={formik.values.from}
                onChange={(value) => {
                  formik.setFieldValue('from', value);
                  formik.setFieldTouched('from', !moment(value).isSame(formik.values.from));
                }}
                disablePast
                slotProps={{
                  textField: {
                    name: 'from',
                    error: formik.touched.from && Boolean(formik.errors.from),
                    helperText:
                      formik.touched.from && formik.errors.from ? String(formik.errors.from) : undefined,
                    fullWidth: true,
                  },
                }}
              />
              <DateTimePicker
                label={t('event.label.to') + ' *'}
                value={formik.values.to}
                onChange={(value) => {
                  formik.setFieldValue('to', value);
                  formik.setFieldTouched('to', !moment(value).isSame(formik.values.to));
                }}
                disablePast
                slotProps={{
                  textField: {
                    name: 'to',
                    error: formik.touched.to && Boolean(formik.errors.to),
                    helperText: formik.touched.to && formik.errors.to ? String(formik.errors.to) : undefined,
                    fullWidth: true,
                  },
                }}
              />
            </Stack>
            <FormControl
              fullWidth
              error={formik.touched.description && Boolean(formik.errors.description)}
              onFocus={() => setIsFieldFocused(true)}
              onBlur={() => setIsFieldFocused(false)}
            >
              <ReactQuill
                theme="snow"
                value={formik.values.description || ''}
                onChange={(content) => {
                  formik.setFieldValue('description', content);
                  formik.setFieldTouched('description', content !== formik.values.description);
                }}
                placeholder={t(`event.placeholder.description`, { ns: 'field' })}
                modules={{
                  toolbar: [
                    [{ header: [false, 3] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                  ],
                }}
              />
              <FormHelperText>
                {formik.touched.description && formik.errors.description
                  ? String(formik.errors.description)
                  : undefined}
              </FormHelperText>
            </FormControl>
            <FormControl
              required
              fullWidth
              error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
            >
              <InputLabel id="category-select-label">{t(`event.label.category`)}</InputLabel>
              <Select
                required
                labelId="category-select-label"
                id="category-select"
                value={formik.values.categoryId || ''}
                name="categoryId"
                label="Category"
                onChange={formik.handleChange}
              >
                {[
                  { id: '', name: t(`event.placeholder.category`) },
                  ...data.eventCategories.map((category) => ({
                    id: category.id,
                    name: t(`category.${category.name}`, { ns: 'event' }),
                  })),
                ].map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formik.errors.categoryId ? String(formik.errors.categoryId) : undefined}
              </FormHelperText>
            </FormControl>
            <SearchParticipants participants={participants} onValidate={setParticipants} />
          </Form.Body>
        </form>
        <Form.Footer hide={false}>
          <Button
            loading={creating}
            fullWidth={Device.isMobile}
            onClick={handleSubmit}
            type="submit"
            variant="contained"
            color="success"
            disabled={!(formik.isValid && formik.dirty)}
          >
            {t('save', { ns: 'cta' })}
          </Button>
        </Form.Footer>
      </Form>
    </Dialog>
  );
}
